import api, { cachedGet } from './axiosConfig';

// Cache keys
const CACHE_KEYS = {
  TOP_SELLING: 'gods_garden_cache_top_selling',
  NEW_ARRIVALS: 'gods_garden_cache_new_arrivals',
  CATEGORIES: 'gods_garden_cache_categories',
  INSTAGRAM: 'gods_garden_cache_instagram',
};

// Cache durations (in milliseconds)
const CACHE_DURATION = {
  PRODUCTS: 10 * 60 * 1000, // 10 minutes
  CATEGORIES: 60 * 60 * 1000, // 1 hour
  INSTAGRAM: 30 * 60 * 1000, // 30 minutes
};

/**
 * Get top selling products
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Array>} Array of top selling products
 */
export const getTopSellingProducts = async (useCache = true) => {
  if (useCache) {
    const result = await cachedGet(
      '/get-top-selling-product/',
      CACHE_KEYS.TOP_SELLING,
      CACHE_DURATION.PRODUCTS
    );
    return result.data?.data || result.data || [];
  }
  const response = await api.get('/get-top-selling-product/');
  return response.data?.data || response.data || [];
};

/**
 * Get new arrival products
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Array>} Array of new arrival products
 */
export const getNewArrivalProducts = async (useCache = true) => {
  if (useCache) {
    const result = await cachedGet(
      '/get-new-arrival-product/',
      CACHE_KEYS.NEW_ARRIVALS,
      CACHE_DURATION.PRODUCTS
    );
    return result.data?.data || result.data || [];
  }
  const response = await api.get('/get-new-arrival-product/');
  return response.data?.data || response.data || [];
};

/**
 * Get reviews for a product
 * @param {number|string} productPk - Product primary key
 * @returns {Promise<Array>} Array of reviews
 */
export const getProductReviews = async (productPk) => {
  const response = await api.get(`/get-all-reviews/?product_pk=${productPk}`);
  return response.data?.data || response.data || [];
};

/**
 * Create a review for a product
 * @param {object} payload - Review payload
 * @returns {Promise<object>} Created review
 */
export const createProductReview = async (payload) => {
  const hasFileUpload = payload?.image instanceof File || payload?.image_2 instanceof File;

  let response;
  if (hasFileUpload) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    const multipartHeaders = { ...api.defaults.headers.common };
    delete multipartHeaders['Content-Type'];
    delete multipartHeaders['content-type'];

    response = await api.post('/create-review/', formData, {
      headers: multipartHeaders,
    });
  } else {
    response = await api.post('/create-review/', payload);
  }

  return response.data?.data || response.data;
};

/**
 * Get product categories
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Array>} Array of categories
 */
export const getProductCategories = async (useCache = true) => {
  if (useCache) {
    const result = await cachedGet(
      '/product-category/',
      CACHE_KEYS.CATEGORIES,
      CACHE_DURATION.CATEGORIES
    );
    return result.data?.data || result.data || [];
  }
  const response = await api.get('/product-category/');
  return response.data?.data || response.data || [];
};

/**
 * Get all products with optional category filter
 * @param {number|null} categoryPk - Category ID to filter by
 * @returns {Promise<Array>} Array of products
 */
export const getAllProducts = async (categoryPk = null) => {
  const response = await api.get('/get-all-products/');
  let products = response.data?.data || response.data || [];

  if (categoryPk != null) {
    const categoryId = Number(categoryPk);
    products = products.filter(
      (product) => Number(product.category_id) === categoryId
    );
  }

  return products;
};

/**
 * Get products by IDs (filters from get-all-products; no dedicated backend endpoint)
 * @param {Array<number|string>} productIds - Array of product IDs
 * @returns {Promise<Array>} Array of product details
 */
export const getProductsByIds = async (productIds) => {
  if (!productIds?.length) {
    return [];
  }

  const idSet = new Set(productIds.map((id) => Number(id)));
  const products = await getAllProducts();

  return products.filter((product) => idSet.has(Number(product.id)));
};

export const getProductImageById = async (productId) => {
  if (!productId) return '';

  try {
    const products = await getAllProducts();
    const product = products.find((item) => Number(item.id) === Number(productId));

    return (
      product?.main_image ||
      product?.image ||
      product?.product_image ||
      product?.thumbnail ||
      ''
    );
  } catch (error) {
    console.error('Failed to fetch product image:', error);
    return '';
  }
};

/**
 * Get Instagram profile posts
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Array>} Array of Instagram posts
 */
export const getInstagramProfile = async (useCache = true) => {
  if (useCache) {
    const result = await cachedGet(
      '/instagram-profile/',
      CACHE_KEYS.INSTAGRAM,
      CACHE_DURATION.INSTAGRAM
    );
    return result.data?.data || result.data || [];
  }
  const response = await api.get('/instagram-profile/');
  return response.data?.data || response.data || [];
};

export { CACHE_KEYS };
